import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { Plus } from 'lucide-react'
import { api } from '@convex/_generated/api'
import { Button } from '@/components/ui/button'
import { parseCapture } from '@convex/model/capture'
import { todayIn } from '@convex/model/dates'
import { formatDateChip } from '@/lib/formatDate'
import { useInboxFocus } from './inboxHotkey'

/**
 * One field, no decisions. Capture that takes longer than a couple of seconds
 * stops happening, and the thought is lost instead of badly filed.
 *
 * Dates are read out of the wording — "call dentist tomorrow" keeps the date
 * and drops the word — so there is never a reason to open a picker here.
 */
export function QuickCapture({
  autoFocus = false,
  onCaptured,
}: {
  autoFocus?: boolean
  /** Fired after a successful capture, so a host can count or close. */
  onCaptured?: () => void
}) {
  const capture = useMutation(api.items.capture)
  const settings = useQuery(api.settings.get, {})
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  /*
   * Focus on request, for the `i` shortcut.
   *
   * `autoFocus` only fires on mount, so pressing `i` while already on the
   * inbox did nothing at all — the route did not change, so nothing remounted.
   * Focus lives outside React, which is what an effect is for.
   */
  const field = useRef<HTMLInputElement>(null)
  const focusNonce = useInboxFocus((state) => state.nonce)
  useEffect(() => {
    if (!autoFocus || focusNonce === 0) return
    field.current?.focus()
  }, [autoFocus, focusNonce])

  /**
   * What the parser will make of this, shown before it is committed.
   *
   * The placeholder invites natural language and the parser understands only
   * dates, so the difference between what you typed and what was understood
   * used to surface as a surprise in the inbox. Reading it here is the same
   * function the server runs, so the preview cannot disagree with the result.
   */
  const preview =
    settings === undefined || text.trim().length === 0
      ? null
      : parseCapture(text, {
          today: todayIn(
            settings.timeZone,
            Date.now(),
            settings.dayStartsAtMinutes,
          ),
          weekStartsOn: settings.weekStartsOn,
        })

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmed = text.trim()
    if (trimmed.length === 0) return

    // Cleared before the round trip so the next thought can go straight in.
    setText('')
    setError(null)
    try {
      await capture({ text: trimmed })
      onCaptured?.()
    } catch {
      setText(trimmed)
      setError('Could not save that. Check your connection.')
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="border-input focus-within:border-muted-foreground/50 flex items-center gap-2 rounded-md border px-3 transition-colors">
        <Plus className="text-muted-foreground size-4 shrink-0" aria-hidden />
        <input
          ref={field}
          autoFocus={autoFocus}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Capture anything — try “call dentist tomorrow”"
          aria-label="Capture"
          className="placeholder:text-muted-foreground min-h-11 w-full bg-transparent text-sm outline-none sm:min-h-10"
        />
        {preview?.date !== undefined && (
          // Says what was understood before it is committed. Without it the
          // gap between what you typed and what was parsed only showed up
          // later, in the inbox, with the wording already rewritten.
          <span className="text-muted-foreground shrink-0 text-xs">
            {formatDateChip(
              preview.date,
              todayIn(
                settings?.timeZone ?? 'UTC',
                Date.now(),
                settings?.dayStartsAtMinutes ?? 0,
              ),
            )}
          </span>
        )}
        {text.trim().length > 0 && (
          <Button type="submit" size="sm" className="my-1.5 shrink-0">
            Add
          </Button>
        )}
      </div>
      {error !== null && (
        <p role="alert" className="text-destructive mt-2 text-sm">
          {error}
        </p>
      )}
    </form>
  )
}
